import type { UUID } from '@/types/Id'
import type { OpportunityType } from '@/types/Opportunity'

/**
 * A downloaded Word file, and which version of the stored file it is.
 *
 * One cache entry, because a save has to send the version of the bytes it
 * edited: bytes paired with somebody else's version would be taken for current
 * and merged over a comment that landed since. Written together, dropped together.
 */
export interface StoredDocx {
  blob: Blob
  /** Null from a path that took the bytes from somewhere other than the file. */
  version: string | null
  /**
   * Which version of the file's history these bytes are, so a copy held while
   * somebody else saved can tell that it has fallen behind. Null where they were
   * taken before the file had one — a fresh copy, or a file stored before the
   * history existed — which the first save on the file turns into a version,
   * leaving them behind it. An upload is not one of those: the server records
   * it as the file's first version, and the bytes carry that id.
   */
  versionId: string | null
}

/** One version of a Word file, as somewhere to read its bytes from. */
export interface DocxVersionSource {
  /** The version these bytes are, which is what the cache is keyed on. */
  id: string
  /** A presigned link to them. */
  url: string
}

interface FileQueryKeysArgs {
  // TODO: change to UUID
  fileId: string
}

export const fileQueryKeys = {
  file: (args: FileQueryKeysArgs) => ['file', args.fileId] as const,
  preview: (args: FileQueryKeysArgs) =>
    ['file', args.fileId, 'preview'] as const,
  /**
   * The downloaded DOCX. Shared: the importer reads the bytes to check the file
   * really is one, and the editor then opens the copy already in hand.
   */
  docx: (args: FileQueryKeysArgs) => ['file', args.fileId, 'docx'] as const,
  /**
   * The file record a DOCX download in progress is reading; null when none
   * is. Set by the download, so the opening a large file shows first is read
   * from the revision the download brings.
   */
  docxRecord: (args: FileQueryKeysArgs) =>
    ['file', args.fileId, 'docx-record'] as const,
  /**
   * A revision of an Office file cut down to its opening, by one slimmer; see
   * usePartialPackage. Keyed on the revision rather than the presigned URL,
   * which is re-signed without the bytes changing.
   */
  partialPackage: (
    args: FileQueryKeysArgs & {
      revision: string
      slimmer: number
      rangesOnly: boolean
    },
  ) =>
    [
      'file',
      args.fileId,
      'partial-package',
      args.revision,
      args.slimmer,
      args.rangesOnly,
    ] as const,
  /** The file's version history, newest first. */
  versions: (args: FileQueryKeysArgs) =>
    ['file', args.fileId, 'versions'] as const,
  /** One of those versions, with links to the bytes it held. */
  version: (args: FileQueryKeysArgs & { versionId: string }) =>
    ['file', args.fileId, 'versions', args.versionId] as const,
  /**
   * The bytes one version holds. Beside the version's record rather than under
   * it: the record is refetched to keep its links alive; the bytes never change.
   */
  versionDocx: (args: FileQueryKeysArgs & { versionId: string }) =>
    ['file', args.fileId, 'version-docx', args.versionId] as const,
  /**
   * One version marked up with what a later one changed. Keyed on the pair and
   * their order: which of the two carries the marks is half of what it is.
   */
  comparison: (
    args: FileQueryKeysArgs & {
      baseVersionId: string
      targetVersionId: string
    },
  ) =>
    [
      'file',
      args.fileId,
      'comparison',
      args.baseVersionId,
      args.targetVersionId,
    ] as const,
}

interface OpportunityFilesQueryKeysArgs {
  opportunityId: UUID | ''
  opportunityType: OpportunityType
}

export const filesQueryKeys = {
  opportunity: (args: OpportunityFilesQueryKeysArgs) =>
    ['opportunity', args.opportunityType, args.opportunityId] as const,
  agentSession: (args: { sessionId: UUID | '' }) =>
    ['agent-session-files', args.sessionId] as const,
  // Under the files key, so every session files invalidation refetches the
  // folders that place them.
  agentSessionFolders: (args: { sessionId: UUID | '' }) =>
    ['agent-session-files', args.sessionId, 'folders'] as const,
}

interface UserOrganizationFilesQueryKeysArgs {
  orgId: UUID | ''
}

interface UserAllOrOrganizationFilesQueryKeysArgs {
  orgId?: UUID | ''
  fileIds?: UUID[]
}

interface UserFileIndexStatusQueryKeyArgs {
  orgId?: UUID | ''
  fileId: UUID
}

export const userFilesQueryKeys = {
  all: () => ['user-files'] as const,
  organization: (args: UserOrganizationFilesQueryKeysArgs) =>
    ['user-files', 'organization', args.orgId] as const,
  allOrOrganization: (args: UserAllOrOrganizationFilesQueryKeysArgs) => {
    if (args.orgId) {
      return ['user-files', 'organization', args.orgId] as const
    }
    return ['user-files'] as const
  },
}

export const userFilesIndexStatusQueryKeys = {
  all: () => ['user-files-index-status'] as const,
  organization: (args: UserOrganizationFilesQueryKeysArgs) =>
    ['user-files-index-status', 'organization', args.orgId] as const,
  allOrOrganization: (args: UserAllOrOrganizationFilesQueryKeysArgs) => {
    if (args.orgId) {
      return ['user-files-index-status', 'organization', args.orgId] as const
    }
    return ['user-files-index-status'] as const
  },
  filtered: (args: UserAllOrOrganizationFilesQueryKeysArgs) =>
    [
      ...userFilesIndexStatusQueryKeys.allOrOrganization(args),
      [...(args.fileIds ?? [])].toSorted().join(','),
    ] as const,
  file: (args: UserFileIndexStatusQueryKeyArgs) =>
    [
      ...userFilesIndexStatusQueryKeys.allOrOrganization(args),
      'file',
      args.fileId,
    ] as const,
}

// region: file copy

interface FileCopyLookupQueryKeyArgs {
  fileIds: UUID[]
}

interface FileCopyLookupFileQueryKeyArgs {
  fileId: UUID
}

export const fileCopyLookupQueryKeys = {
  all: () => ['file-copy-lookup'] as const,
  files: (keyArgs: FileCopyLookupQueryKeyArgs) =>
    ['file-copy-lookup', ...[...keyArgs.fileIds].sort()] as const,
  file: (keyArgs: FileCopyLookupFileQueryKeyArgs) =>
    ['file-copy-lookup', keyArgs.fileId] as const,
}

// endregion: file copy

// region: file presence

interface FilePresenceQueryKeyArgs {
  fileId: UUID | ''
}

export const filePresenceQueryKeys = {
  all: () => ['file-presence'] as const,
  /** Whoever is in one file's editor room. */
  file: (args: FilePresenceQueryKeyArgs) =>
    ['file-presence', args.fileId] as const,
}

// endregion: file presence
